// src/components/SearchHistoryPanel.jsx
// Right-side slide-in panel with Recent / Saved / Watchlist sections.
// Data + handlers come from props. Same enterprise styling pattern as the
// filter drawer — flat panel, hairline section dividers, uppercase eyebrows,
// 12–13px typography.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  Search,
  Clock,
  Bookmark,
  Eye,
  ChevronDown,
  ChevronRight,
  Trash2,
} from "lucide-react";

// Server returns everything in one shot. These caps are belt-and-suspenders.
const RECENT_CAP = 50;

export const INITIAL_SAVED = [
  { id: "s1", name: "Critical exposures – last 7d", query: "*.acme.com", searchType: "consumerRecords", type: "domain" },
  { id: "s2", name: "Contractor leaks", query: "*.contractor.acme.com", searchType: "consumerRecords", type: "domain" },
  { id: "s3", name: "Exec emails", query: "ceo@acme.com", searchType: "emailAddress", type: "email" },
  { id: "s4", name: "Partner portal", query: "partner-portal.acme.com", searchType: "consumerRecords", type: "subdomain" },
  { id: "s5", name: "Marriott infosec", query: "marriott.com", searchType: "consumerRecords", type: "domain" },
  { id: "s6", name: "Internal IPs", query: "10.0.0.0/8", searchType: "ipAddress", type: "ip" },
  { id: "s7", name: "GitHub mirror", query: "github.com", searchType: "consumerRecords", type: "domain" },
  { id: "s8", name: "VPN exposure", query: "vpn.acme.com", searchType: "consumerRecords", type: "subdomain" },
  { id: "s9", name: "Auth subdomain", query: "auth.acme.com", searchType: "consumerRecords", type: "subdomain" },
  { id: "s10", name: "Admin accounts", query: "admin@acme.com", searchType: "emailAddress", type: "email" },
  { id: "s11", name: "Stealer matches – marriott", query: "*.marriott.com", searchType: "consumerRecords", type: "domain" },
  { id: "s12", name: "API surface", query: "api.acme.com", searchType: "consumerRecords", type: "subdomain" },
  { id: "s13", name: "Billing leaks", query: "billing@acme.com", searchType: "emailAddress", type: "email" },
  { id: "s14", name: "Staging environments", query: "staging.acme.com", searchType: "consumerRecords", type: "subdomain" },
  { id: "s15", name: "Old contractor scope", query: "old-contractor.acme.com", searchType: "consumerRecords", type: "subdomain" },
  { id: "s16", name: "Q1 audit set", query: "audit-q1.acme.com", searchType: "consumerRecords", type: "subdomain" },
  { id: "s17", name: "Sales aliases", query: "sales@acme.com", searchType: "emailAddress", type: "email" },
  { id: "s18", name: "Dev environments", query: "dev.acme.com", searchType: "consumerRecords", type: "subdomain" },
];

export default function SearchHistoryPanel({
  open,
  onClose,
  onSelect,
  recent = [],
  saved = [],
  watchlist = [],
  onRemoveSaved,
  onClearRecent,
  onClearSaved,
  // Highlight the row that matches the user's current active search
  activeQuery = "",
  activeSearchType = "",
  // Server-fetched async state
  recentLoading = false,
  recentError = "",
  onDismissRecentError,
  savedLoading = false,
  savedError = "",
  onDismissSavedError,
  watchlistLoading = false,
  watchlistError = "",
  onDismissWatchlistError,
}) {
  const [filter, setFilter] = useState("");
  // Recent defaults to expanded — it's the highest-value content when an
  // analyst opens the panel. Saved and Watchlist stay collapsed.
  const [sections, setSections] = useState({
    recent: true,
    saved: false,
    watchlist: false,
  });
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const f = filter.trim().toLowerCase();

  const recentFiltered = useMemo(() => {
    const capped = recent.slice(0, RECENT_CAP);
    return f ? capped.filter((x) => x.query.toLowerCase().includes(f)) : capped;
  }, [f, recent]);

  const savedFiltered = useMemo(
    () =>
      f
        ? saved.filter(
            (x) =>
              x.query.toLowerCase().includes(f) ||
              (x.typeLabel || "").toLowerCase().includes(f)
          )
        : saved,
    [f, saved]
  );

  const watchlistFiltered = useMemo(
    () =>
      f
        ? watchlist.filter((x) => x.label.toLowerCase().includes(f))
        : watchlist,
    [f, watchlist]
  );

  // No pagination — server returns all items, render all.
  const savedShown = savedFiltered;
  const watchlistShown = watchlistFiltered;

  const toggle = (k) => setSections((s) => ({ ...s, [k]: !s[k] }));

  return (
    <aside
      aria-hidden={!open}
      className={`fixed top-0 right-0 z-[60] h-screen w-[340px] flex flex-col border-l border-sky-900/40 bg-[#0a1224]
        transform transition-transform duration-200 ease-out
        ${open ? "translate-x-0" : "translate-x-full pointer-events-none"}`}
    >
      {/* Header — uppercase tracked, lucide X close. Matches the filter drawer. */}
      <div className="flex items-center justify-between border-b border-sky-900/30 px-4 py-3">
        <h3 className="text-[12px] font-semibold uppercase tracking-[0.18em] text-neutral-200">
          Activity
        </h3>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md text-neutral-400 hover:text-white hover:bg-white/[0.04] transition-colors"
          aria-label="Close activity panel"
          type="button"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Search filter — consistent 13px input. */}
      <div className="px-4 py-3 border-b border-sky-900/30">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" />
          <input
            ref={inputRef}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter recent, saved, watchlist…"
            className="w-full rounded-md border border-sky-900/30 bg-neutral-900/60 pl-9 pr-3 py-2 text-[13px] text-white placeholder-neutral-500 outline-none focus:border-sky-500/50 transition-colors"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <Section
          label="Recent searches"
          icon={Clock}
          count={recentFiltered.length}
          isOpen={sections.recent}
          onToggle={() => toggle("recent")}
          action={
            onClearRecent && sections.recent ? (
              <ClearAllButton
                onClick={onClearRecent}
                disabled={recent.length === 0}
              />
            ) : null
          }
        >
          {recentError ? (
            <ErrorRow message={recentError} onDismiss={onDismissRecentError} />
          ) : recentLoading && recentFiltered.length === 0 ? (
            <LoadingRow />
          ) : recentFiltered.length === 0 ? (
            <Empty />
          ) : (
            recentFiltered.map((r) => (
              <Row
                key={r.id}
                primary={r.query}
                meta={`${r.type} · ${r.at}`}
                selected={
                  r.query === activeQuery &&
                  r.searchType === activeSearchType
                }
                onClick={() =>
                  onSelect?.({
                    query: r.query,
                    searchType: r.searchType,
                    type: r.type,
                  })
                }
              />
            ))
          )}
        </Section>

        <Section
          label="Saved searches"
          icon={Bookmark}
          count={savedFiltered.length}
          isOpen={sections.saved}
          onToggle={() => toggle("saved")}
          action={
            onClearSaved && sections.saved ? (
              <ClearAllButton
                onClick={onClearSaved}
                disabled={saved.length === 0}
              />
            ) : null
          }
        >
          {savedError ? (
            <ErrorRow message={savedError} onDismiss={onDismissSavedError} />
          ) : savedLoading && savedShown.length === 0 ? (
            <LoadingRow />
          ) : savedShown.length === 0 ? (
            <Empty />
          ) : (
            savedShown.map((s) => (
              <Row
                key={s.id}
                primary={s.query}
                meta={s.typeLabel}
                selected={
                  s.query === activeQuery &&
                  s.searchType === activeSearchType
                }
                onClick={() =>
                  onSelect?.({
                    query: s.query,
                    searchType: s.searchType,
                    type: s.type,
                  })
                }
                onRemove={
                  onRemoveSaved ? () => onRemoveSaved(s.id) : undefined
                }
                removeTitle="Remove saved search"
              />
            ))
          )}
        </Section>

        <Section
          label="Watchlist"
          icon={Eye}
          count={watchlistFiltered.length}
          isOpen={sections.watchlist}
          onToggle={() => toggle("watchlist")}
        >
          {watchlistError ? (
            <ErrorRow message={watchlistError} onDismiss={onDismissWatchlistError} />
          ) : watchlistLoading && watchlistShown.length === 0 ? (
            <LoadingRow />
          ) : watchlistShown.length === 0 ? (
            <Empty />
          ) : (
            watchlistShown.map((w) => (
              <Row
                key={w.id}
                primary={w.label}
                meta={w.type}
                selected={
                  w.label === activeQuery &&
                  (w.type === "domain"
                    ? activeSearchType === "consumerRecords" ||
                      activeSearchType === "corporateRecords"
                    : w.searchType === activeSearchType)
                }
                onClick={() =>
                  onSelect?.({
                    query: w.label,
                    searchType: w.searchType,
                    type: w.type,
                  })
                }
                actions={
                  w.type === "domain"
                    ? [
                        {
                          label: "Consumer",
                          title: "Search Consumer Records",
                          onClick: () =>
                            onSelect?.({
                              query: w.label,
                              searchType: "consumerRecords",
                              type: w.type,
                            }),
                        },
                        {
                          label: "Corporate",
                          title: "Search Corporate Records",
                          onClick: () =>
                            onSelect?.({
                              query: w.label,
                              searchType: "corporateRecords",
                              type: w.type,
                            }),
                        },
                      ]
                    : null
                }
              />
            ))
          )}
        </Section>
      </div>

      {/* Footer — single quiet shortcut line. */}
      <div className="border-t border-sky-900/30 px-4 py-2.5 text-[11px] text-neutral-500 font-mono">
        <kbd className="px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/10 text-neutral-300">
          Ctrl K
        </kbd>
        <span className="ml-2">to toggle · Esc to close</span>
      </div>
    </aside>
  );
}

function Section({ label, icon: Icon, count, isOpen, onToggle, action, children }) {
  return (
    <div className="border-b border-sky-900/20">
      <div className="flex items-center justify-between">
        <button
          onClick={onToggle}
          className="flex-1 min-w-0 flex items-center gap-2 px-4 py-2.5 text-left text-neutral-300 hover:text-white hover:bg-white/[0.02] transition-colors"
        >
          {isOpen ? (
            <ChevronDown className="h-3 w-3 text-neutral-500 shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 text-neutral-500 shrink-0" />
          )}
          {Icon && <Icon className="h-3 w-3 text-neutral-500 shrink-0" />}
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">
            {label}
          </span>
          <span className="text-[10px] text-neutral-500 font-mono ml-auto">
            {count}
          </span>
        </button>
        {action && <div className="pr-2">{action}</div>}
      </div>
      {isOpen && <div className="border-t border-sky-900/20">{children}</div>}
    </div>
  );
}

function ClearAllButton({ onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 hover:text-rose-300 disabled:text-neutral-700 disabled:hover:text-neutral-700 px-2 py-1 transition-colors"
    >
      Clear
    </button>
  );
}

function Row({ primary, meta, trailing, onClick, onRemove, removeTitle, selected, actions }) {
  const hasActions = actions && actions.length > 0;
  return (
    <div
      className={`group relative flex items-stretch transition-colors ${
        selected ? "bg-sky-500/[0.06]" : "hover:bg-white/[0.02]"
      }`}
    >
      {selected && (
        <span
          className="absolute left-0 top-0 bottom-0 w-0.5 bg-sky-400"
          aria-hidden="true"
        />
      )}
      <button
        onClick={hasActions ? actions[0].onClick : onClick}
        className="flex-1 min-w-0 text-left flex items-center justify-between gap-2 px-4 py-2"
      >
        <div className="min-w-0 flex-1">
          <div
            className={`text-[13px] leading-tight truncate font-mono ${
              selected ? "text-sky-100" : "text-white"
            }`}
          >
            {primary}
          </div>
          {meta && (
            <div className="mt-0.5 text-[11px] text-neutral-500 truncate uppercase tracking-wider">
              {meta}
            </div>
          )}
        </div>
        {trailing && <div className={onRemove ? "mr-6" : ""}>{trailing}</div>}
      </button>
      {hasActions && (
        <div className="flex items-center gap-1 pr-2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
          {actions.map((a, i) => (
            <button
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                a.onClick();
              }}
              title={a.title}
              aria-label={a.title}
              className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium text-neutral-400 hover:text-sky-200 hover:bg-white/[0.04] transition-colors whitespace-nowrap"
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          title={removeTitle}
          aria-label={removeTitle}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-neutral-500 opacity-0 group-hover:opacity-100 hover:text-rose-300 hover:bg-rose-500/10 transition-opacity"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function Empty() {
  return (
    <div className="px-4 py-3 text-[12px] text-neutral-500">No matches.</div>
  );
}

function ErrorRow({ message, onDismiss }) {
  return (
    <div className="px-4 py-3 text-[12px] text-rose-300 flex items-center justify-between gap-2">
      <span className="min-w-0 truncate">Failed to load: {message}</span>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="shrink-0 p-1 rounded text-rose-300/70 hover:text-rose-200 hover:bg-rose-500/10 transition-colors"
          aria-label="Dismiss error"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function LoadingRow() {
  return (
    <div className="space-y-1.5 py-2 px-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="space-y-1.5">
          <div className="h-3 rounded bg-white/[0.04] animate-pulse w-3/4" />
          <div className="h-2.5 rounded bg-white/[0.04] animate-pulse w-1/3" />
        </div>
      ))}
    </div>
  );
}
